import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera, RotateCcw, AlertCircle, Loader2 } from "lucide-react";

interface WebcamCaptureProps {
  onCapture: (blob: Blob) => void;
  capturedUrl?: string | null;
}

const WebcamCapture = ({ onCapture, capturedUrl }: WebcamCaptureProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraActive(false);
  }, []);

  const startCamera = async () => {
    setIsStarting(true);
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Câmera não suportada neste navegador.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 480 }, height: { ideal: 360 }, facingMode: "user" },
      });
      streamRef.current = stream;
      setIsCameraActive(true);
    } catch (err: any) {
      const msg = err.name === "NotAllowedError"
        ? "Permissão negada. Permita o acesso à câmera."
        : err.name === "NotFoundError"
        ? "Nenhuma câmera encontrada."
        : err.message || "Erro ao acessar câmera.";
      setError(msg);
    } finally {
      setIsStarting(false);
    }
  };

  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [isCameraActive]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        onCapture(blob);
        stopCamera();
      }
    }, "image/jpeg", 0.85);
  };

  if (capturedUrl) {
    return (
      <div className="space-y-2">
        <div className="rounded-lg overflow-hidden border border-border bg-muted aspect-[4/3]">
          <img src={capturedUrl} alt="Captura" className="w-full h-full object-cover" />
        </div>
        <Button variant="outline" size="sm" onClick={() => { startCamera(); }} className="w-full gap-1.5">
          <RotateCcw className="h-3.5 w-3.5" />
          Nova Captura
        </Button>
      </div>
    );
  }

  if (isCameraActive) {
    return (
      <div className="space-y-2">
        <div className="rounded-lg overflow-hidden bg-black aspect-[4/3] relative">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="flex gap-2">
          <Button onClick={capture} className="flex-1 gap-1.5" size="sm">
            <Camera className="h-3.5 w-3.5" /> Capturar
          </Button>
          <Button variant="outline" size="sm" onClick={stopCamera}>Cancelar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 aspect-[4/3] flex flex-col items-center justify-center gap-2 p-4">
        <Camera className="h-8 w-8 text-muted-foreground" />
        <p className="text-xs text-muted-foreground text-center">Câmera de identificação</p>
        {error && (
          <div className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
      <Button onClick={startCamera} disabled={isStarting} variant="outline" size="sm" className="w-full gap-1.5">
        {isStarting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
        {isStarting ? "Acessando..." : "Iniciar Câmera"}
      </Button>
    </div>
  );
};

export default WebcamCapture;
